'use client'

declare global {
  interface Window {
    turnstile: {
      render: (element: HTMLElement, options: any) => string
      reset: (widgetId: string) => void
    }
  }
}

import { useState, useRef, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import Script from 'next/script'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import Link from 'next/link'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    interests: {
      marketIntelligence: false,
      competitorAnalysis: false,
      contractOpportunities: false,
      regulatoryCompliance: false,
      aiSolutions: false,
      other: false
    },
    message: '',
    otherInterest: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileReady, setTurnstileReady] = useState(false)
  const turnstileRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  const onTurnstileLoad = useCallback(() => {
    setTurnstileReady(true)
  }, [])

  useEffect(() => {
    if (turnstileReady && turnstileRef.current && window.turnstile && !widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(null),
      })
    }
  }, [turnstileReady])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)
    setErrorMessage('')

    if (!turnstileToken) {
      setSubmitStatus('error')
      setErrorMessage('Please complete the captcha verification.')
      setIsErrorModalOpen(true)
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      setSubmitStatus('success');
      setIsSuccessModalOpen(true);
      setTurnstileToken(null);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        interests: {
          marketIntelligence: false,
          competitorAnalysis: false,
          contractOpportunities: false,
          regulatoryCompliance: false,
          aiSolutions: false,
          other: false
        },
        message: '',
        otherInterest: ''
      });

    } catch (error: any) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || "Something went wrong. Please try again.");
      setIsErrorModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    
    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement
      setFormData(prev => ({
        ...prev,
        interests: {
          ...prev.interests,
          [name.replace('interest-', '')]: checkbox.checked
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  return (
    <>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        onLoad={onTurnstileLoad}
      />
      <div className="container max-w-2xl mx-auto px-4 py-16">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Contact Our Experts</h1>
          <p className="text-muted-foreground">
            Tell us about your needs and we'll help you find the right solution
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="firstName" className="block text-sm font-medium">
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full p-2 rounded-md border border-input bg-background"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="lastName" className="block text-sm font-medium">
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full p-2 rounded-md border border-input bg-background"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 rounded-md border border-input bg-background"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="phone" className="block text-sm font-medium">
                Phone Number
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-2 rounded-md border border-input bg-background"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="company" className="block text-sm font-medium">
              Company
            </label>
            <input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              className="w-full p-2 rounded-md border border-input bg-background"
              required
            />
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium">
              What are you interested in? (Select all that apply)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="interest-marketIntelligence"
                  checked={formData.interests.marketIntelligence}
                  onChange={handleChange}
                  className="rounded border-input"
                />
                <span>Market Intelligence</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="interest-competitorAnalysis"
                  checked={formData.interests.competitorAnalysis}
                  onChange={handleChange}
                  className="rounded border-input"
                />
                <span>Competitor Analysis</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="interest-contractOpportunities"
                  checked={formData.interests.contractOpportunities}
                  onChange={handleChange}
                  className="rounded border-input"
                />
                <span>Contract Opportunities</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="interest-regulatoryCompliance"
                  checked={formData.interests.regulatoryCompliance}
                  onChange={handleChange}
                  className="rounded border-input"
                />
                <span>Regulatory Compliance</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="interest-aiSolutions"
                  checked={formData.interests.aiSolutions}
                  onChange={handleChange}
                  className="rounded border-input"
                />
                <span>AI Solutions</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="interest-other"
                  checked={formData.interests.other}
                  onChange={handleChange}
                  className="rounded border-input"
                />
                <span>Other</span>
              </label>
            </div>
          </div>

          {formData.interests.other && (
            <div className="space-y-2">
              <label htmlFor="otherInterest" className="block text-sm font-medium">
                Please specify your other interest
              </label>
              <input
                id="otherInterest"
                name="otherInterest"
                type="text"
                value={formData.otherInterest}
                onChange={handleChange}
                className="w-full p-2 rounded-md border border-input bg-background"
              />
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="message" className="block text-sm font-medium">
              How can we help you?
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full p-2 rounded-md border border-input bg-background resize-none"
              required
            />
          </div>

          <div ref={turnstileRef} className="flex justify-center" />

          <Button type="submit" className="w-full" disabled={isSubmitting || !turnstileToken}>
            {isSubmitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
            ) : (
              'Submit'
            )}
          </Button>
        </form>
      </div>

      <Dialog open={isSuccessModalOpen} onOpenChange={setIsSuccessModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Message Sent!</DialogTitle>
            <DialogDescription>
              Thank you for contacting us. We'll get back to you within 24-48 business hours.
              In the meantime, you can browse our help documentation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start gap-2 pt-4">
            <Button asChild>
              <Link href="/documentation">View Help Documentation</Link>
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isErrorModalOpen} onOpenChange={setIsErrorModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submission Failed</DialogTitle>
            <DialogDescription>
              {errorMessage || "Something went wrong. Please try again or contact support directly."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
} 